<template>
  <div class="demo">
    <!-- Visitor banner -->
    <div class="demo-banner">
      <div class="demo-banner-text">
        <v-icon size="small" class="mr-1">mdi-eye-outline</v-icon>
        <strong>{{ c.banner_title }}</strong> — {{ c.banner_note }}
      </div>
      <div class="demo-banner-actions">
        <v-btn size="small" color="white" variant="flat" class="text-deep-purple" @click="goRegister">
          <v-icon start size="small">mdi-account-plus</v-icon>{{ c.register }}
        </v-btn>
        <v-btn size="small" color="white" variant="outlined" @click="goLogin">{{ c.login }}</v-btn>
        <v-btn size="small" variant="text" color="white" @click="exitDemo">
          <v-icon start size="small">mdi-close</v-icon>{{ c.exit }}
        </v-btn>
      </div>
    </div>

    <!-- Top bar -->
    <header class="demo-topbar">
      <div class="brand">
        <img :src="logo" alt="Veridian" class="brand-logo" />
        <span class="brand-name">Veridian</span>
      </div>
    </header>

    <v-container class="py-8">
      <h1 class="gallery-title">{{ c.title }}</h1>
      <p class="gallery-subtitle">{{ c.subtitle }}</p>

      <!-- Carousel gallery: one screenshot per section, with title + prev/next -->
      <v-card variant="flat" class="gallery-card">
        <!-- Section title -->
        <div class="slide-titlebar">
          <span class="slide-index">{{ index + 1 }} / {{ gallery.length }}</span>
          <span class="slide-title">{{ current.caption[lang] }}</span>
        </div>

        <v-carousel
          v-model="index"
          height="72vh"
          hide-delimiter-background
          show-arrows="always"
          progress="primary"
          :continuous="true"
        >
          <v-carousel-item v-for="(g, i) in gallery" :key="i">
            <div class="slide">
              <v-img :src="g.src" :alt="g.caption[lang]" contain height="72vh" />
            </div>
          </v-carousel-item>
        </v-carousel>

        <!-- Explicit prev / next controls -->
        <div class="gallery-controls">
          <v-btn variant="tonal" color="primary" @click="prev">
            <v-icon start>mdi-chevron-left</v-icon>{{ c.prev }}
          </v-btn>
          <div class="gallery-dots">
            <span
              v-for="(g, i) in gallery"
              :key="i"
              class="dot"
              :class="{ active: i === index }"
              @click="index = i"
            ></span>
          </div>
          <v-btn variant="tonal" color="primary" @click="next">
            {{ c.next }}<v-icon end>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </v-card>

      <p class="text-center text-medium-emphasis mt-8">{{ c.footer_note }}</p>
      <div class="text-center mb-4">
        <v-btn color="deep-purple" size="large" variant="flat" @click="goRegister">
          <v-icon start>mdi-account-plus</v-icon>{{ c.cta_register }}
        </v-btn>
      </div>
    </v-container>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import logo from "@/assets/logo.png";
import { currentLocale } from "@/composables/useLanguages";

const BASE = "/images/manual/manual-demo/";

const GALLERY = [
  { file: "01-login-page.png",             ro: "Autentificare securizată",            en: "Secure sign-in" },
  { file: "10-navbar-open.png",            ro: "Meniu complet de administrare",       en: "Full management menu" },
  { file: "11-my-numbers-empty.png",       ro: "Numerele tale, într-un singur loc",   en: "Your numbers in one place" },
  { file: "12-add-number-dialog.png",      ro: "Caută și alege un număr",             en: "Search and pick a number" },
  { file: "13-add-number-network.png",     ro: "Alege rețeaua și opțiunile",          en: "Choose network and options" },
  { file: "14-add-number-filled.png",      ro: "Confirmă detaliile comenzii",         en: "Confirm the order details" },
  { file: "15-sms-verification.png",       ro: "Verificare prin SMS",                 en: "SMS verification" },
  { file: "18-my-numbers-with-number.png", ro: "Numărul tău este activ",              en: "Your number is active" },
  { file: "09-register-complete.png",      ro: "Cont creat cu succes",                en: "Account created successfully" },
];

const COPY = {
  ro: {
    banner_title: "Mod vizitator",
    banner_note: "previzualizare a platformei. Creează un cont pentru acces complet.",
    register: "Creează cont", login: "Autentificare", exit: "Ieși",
    title: "Tur al platformei",
    subtitle: "Parcurge pas cu pas cum arată platforma în uz.",
    prev: "Înapoi", next: "Înainte",
    footer_note: "Ți-a plăcut? Creează un cont gratuit și începe să-ți gestionezi serviciile.",
    cta_register: "Creează cont gratuit",
  },
  en: {
    banner_title: "Visitor mode",
    banner_note: "a preview of the platform. Create an account for full access.",
    register: "Create account", login: "Sign in", exit: "Exit",
    title: "Platform tour",
    subtitle: "Step through how the platform looks in use.",
    prev: "Back", next: "Next",
    footer_note: "Like what you see? Create a free account and start managing your services.",
    cta_register: "Create a free account",
  },
};

export default defineComponent({
  name: 'DemoView',
  data() {
    return {
      logo,
      gallery: GALLERY.map(g => ({ src: BASE + g.file, caption: { ro: g.ro, en: g.en } })),
      index: 0,
    };
  },
  computed: {
    lang() { return currentLocale.value === 'en' ? 'en' : 'ro'; },
    c() { return COPY[this.lang]; },
    current() { return this.gallery[this.index]; },
  },
  methods: {
    prev() { this.index = (this.index - 1 + this.gallery.length) % this.gallery.length; },
    next() { this.index = (this.index + 1) % this.gallery.length; },
    goLogin() { this.$router.push({ path: "/login" }); },
    goRegister() { this.$router.push({ path: "/login", query: { register: "1" } }); },
    exitDemo() { this.$router.push({ path: "/" }); },
  },
});
</script>

<style scoped>
.demo { min-height: 100vh; background: #f4f5f9; }
.demo-banner {
  position: sticky; top: 0; z-index: 10;
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px;
  padding: 8px 18px; background: #5e35b1; color: #fff; font-size: .9rem;
}
.demo-banner-actions { display: flex; gap: 8px; align-items: center; }
.demo-topbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 24px; background: linear-gradient(90deg, #1565c0, #1976d2); color: #fff;
}
.brand { display: flex; align-items: center; gap: 10px; }
.brand-logo { height: 34px; background: #fff; border-radius: 6px; padding: 2px; }
.brand-name { font-size: 1.2rem; font-weight: 700; }
.gallery-title { text-align: center; font-size: 1.9rem; font-weight: 800; color: #1a2233; }
.gallery-subtitle { text-align: center; color: #5a6275; margin-bottom: 24px; }
.gallery-card { border: 1px solid #eceef3; border-radius: 16px; overflow: hidden; background: #fff; }
.slide-titlebar {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 20px; background: linear-gradient(90deg, #1976d2, #5e35b1); color: #fff;
}
.slide-index { font-weight: 800; background: rgba(255,255,255,.2); padding: 2px 10px; border-radius: 20px; font-size: .85rem; }
.slide-title { font-size: 1.15rem; font-weight: 700; }
.slide { height: 72vh; display: flex; align-items: center; justify-content: center; background: #0f1115; }
.gallery-controls {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 14px 20px; background: #fff; border-top: 1px solid #eceef3;
}
.gallery-dots { display: flex; gap: 8px; flex-wrap: wrap; justify-content: center; }
.dot { width: 10px; height: 10px; border-radius: 50%; background: #cdd3e0; cursor: pointer; transition: all .15s ease; }
.dot.active { background: #5e35b1; transform: scale(1.25); }
</style>
